import React from "react";
import { Alert, Form, Input, Select, Spin } from "antd";
import { formatMoney, formatAccountText } from "../../../utils/formatUtils";

export default function AccountInfoSection({
  title,
  isManual,
  isTransfer,
  selectedAccount,
  manualSenderInstitutionId,
  senderOptions,
  transferScope,
  externalInstitutionsLoading,
  externalInstitutionOptions,
  receiverCurrencyOptions,
  filteredReceiverOptions,
  handleTransferScopeSelect,
  handleReceiverAccountSelect,
}) {
  const accountDetails = (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
      <div className="font-medium text-slate-800">
        {selectedAccount ? formatAccountText(selectedAccount) : "-"}
      </div>

      <div className="mt-1 flex flex-wrap gap-x-4 text-xs text-slate-500">
        <span>
          Үлдэгдэл:{" "}
          <span className="font-medium text-slate-700">
            {selectedAccount
              ? formatMoney(selectedAccount.balance, selectedAccount.currency)
              : "-"}
          </span>
        </span>

        <span>
          Wallet:{" "}
          <span className="font-medium text-slate-700">
            {selectedAccount
              ? formatMoney(
                  selectedAccount.walletBalance,
                  selectedAccount.currency
                )
              : "-"}
          </span>
        </span>
      </div>
    </div>
  );

  return (
    <>
      {isManual && (
        <Alert
          className="mb-5 rounded-xl"
          type="info"
          showIcon
          title="Гараар гүйлгээ хийх дүрэм"
          description={`Хүлээн авагч нь сонгосон данс байна. Илгээгч данснууд нь зөвхөн байгууллага ${manualSenderInstitutionId} болон валют ${selectedAccount?.currency || "-"}-тай байна. Entry type нь үргэлж MANUAL_INCOME байна.`}
        />
      )}

      <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <div className="text-base font-semibold text-slate-800">
              Дансны мэдээлэл
            </div>
            <div className="text-sm text-slate-500">
              {isManual
                ? "Илгээгч дансаа сонгоно уу"
                : "Хүлээн авагч дансаа сонгоно уу."}
            </div>
          </div>

          <div className="rounded-xl bg-slate-50 px-4 py-2 text-sm text-slate-600">
            Илгээгчийн валют:{" "}
            <span className="font-semibold text-slate-900">
              {selectedAccount?.currency || "-"}
            </span>
          </div>
        </div>

        {isManual && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Form.Item
              label={
                <span className="font-medium text-slate-700">Илгээгч данс</span>
              }
              name="senderBankAccountId"
              rules={[{ required: true, message: "Илгээгч дансаа сонгоно уу" }]}
            >
              <Select
                showSearch
                size="large"
                optionFilterProp="searchText"
                filterOption={(input, option) =>
                  (option?.searchText || "")
                    .toString()
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={senderOptions}
                placeholder="Илгээгч дансаа сонгоно уу"
                className="[&_.ant-select-selector]:rounded-xl"
              />
            </Form.Item>

            <Form.Item
              label={
                <span className="font-medium text-slate-700">
                  {`Хүлээн авагч данс (${ title })`}
                </span>
              }
            >
              {accountDetails}
            </Form.Item>

            <Form.Item name="receiverBankAccountId" hidden>
              <Input />
            </Form.Item>
          </div>
        )}

        {isTransfer && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Form.Item
              label={
                <span className="font-medium text-slate-700">
                  Шилжүүлгийн төрөл
                </span>
              }
              name="transferScope"
              rules={[{ required: true, message: "Шилжүүлгийн төрөл сонгоно уу" }]}
            >
              <Select
                size="large"
                options={[
                  { value: "INTERNAL", label: `Дотоод (${title})` },
                  { value: "EXTERNAL", label: "Гадаад (бусад байгууллага хооронд)" },
                ]}
                onChange={handleTransferScopeSelect}
                className="[&_.ant-select-selector]:rounded-xl"
              />
            </Form.Item>

            <Form.Item
              label={
                <span className="font-medium text-slate-700">
                  Хүлээн авагчийг валютаар шүүх
                </span>
              }
              name="receiverCurrencyFilter"
            >
              <Select
                showSearch
                size="large"
                optionFilterProp="label"
                options={receiverCurrencyOptions}
                placeholder="Валют сонгоно уу"
                className="[&_.ant-select-selector]:rounded-xl"
              />
            </Form.Item>

            {transferScope === "EXTERNAL" && (
              <Form.Item
                label={
                  <span className="font-medium text-slate-700">
                    Хүлээн авагч байгууллага
                  </span>
                }
                name="externalInstitutionId"
                rules={[{ required: true, message: "Байгууллага сонгоно уу" }]}
                className="md:col-span-2"
              >
                <Select
                  showSearch
                  size="large"
                  loading={externalInstitutionsLoading}
                  optionFilterProp="label"
                  options={externalInstitutionOptions}
                  placeholder="Байгууллага сонгоно уу"
                  className="[&_.ant-select-selector]:rounded-xl"
                />
              </Form.Item>
            )}

            <Form.Item
              label={<span className="font-medium text-slate-700">{`Илгээгч данс (${ title })`}</span>}
            >
              {accountDetails}
            </Form.Item>

            <Form.Item
              label={<span className="font-medium text-slate-700">Хүлээн авагч данс</span>}
              name="receiverBankAccountId"
              rules={[{ required: true, message: "Хүлээн авагч дансаа сонгоно уу" }]}
            >
              <Select
                showSearch
                size="large"
                loading={transferScope === "EXTERNAL" && externalInstitutionsLoading}
                notFoundContent={
                  transferScope === "EXTERNAL" && externalInstitutionsLoading ? (
                    <div className="py-4 text-center">
                      <Spin size="small" />
                    </div>
                  ) : undefined
                }
                optionFilterProp="searchText"
                filterOption={(input, option) =>
                  (option?.searchText || "")
                    .toString()
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={filteredReceiverOptions}
                onChange={handleReceiverAccountSelect}
                placeholder={
                  transferScope === "EXTERNAL"
                    ? "Өөр байгууллагын хүлээн авагч дансаа сонгоно уу"
                    : "Хүлээн авагч дансаа сонгоно уу"
                }
                className="[&_.ant-select-selector]:rounded-xl"
              />
            </Form.Item>

            <Form.Item name="senderBankAccountId" hidden>
              <Input />
            </Form.Item>
          </div>
        )}
      </div>
    </>
  );
}